/**
 * "Print or PDF" beside Download Word on the helper documents.
 *
 * Takes the same url, postBody and filename as the page's DownloadDocxButton
 * and fetches the same Word file through fetchDocx (one request, the route's
 * own gate and audit write). The browser's print window then opens on the
 * document alone (src/lib/docxPrint.ts), where the person prints or saves a
 * PDF. Cancelling the window is not a failure. A failure to fetch or lay the
 * file out shows the house sentence under the button; Download Word is left
 * alone.
 */

import { useState, type ReactNode } from 'react';
import { fetchDocx } from '@/lib/downloadDocx';
import { Button, type ButtonVariant, type ButtonSize } from './primitives/Button';
import { errorText, FALLBACK_SUPPORT_CONTACT } from '@/lib/errorCopy';
import { loadModule, isModuleLoadError, reportSurfaceFailure } from '@/lib/loadModule';

export interface DocumentPrintPdfButtonProps {
  /** The same URL the page's Download Word button uses. */
  url: string;
  /** The same fallback Word file name; the PDF is offered under its stem. */
  filename: string;
  /** The same POST body, for the stateless Medication List. Absent = GET. */
  postBody?: unknown;
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  testId?: string;
}

export const PRINT_PDF_LABEL = 'Print or PDF';

export function DocumentPrintPdfButton({
  url,
  filename,
  postBody,
  className,
  variant = 'secondary',
  size = 'md',
  testId,
}: DocumentPrintPdfButtonProps): ReactNode {
  const [status, setStatus] = useState<'idle' | 'working' | 'error' | 'stale'>('idle');

  async function handleClick(): Promise<void> {
    setStatus('working');
    try {
      const docx = await fetchDocx(url, filename, postBody);
      const { printDocxFile } = await loadModule('document_print', () => import('@/lib/docxPrint'));
      await printDocxFile(docx);
      setStatus('idle');
    } catch (e) {
      // A page opened before a deploy says to reload (loadModule reported
      // it); any other failure is reported here, without the document.
      if (isModuleLoadError(e)) {
        setStatus('stale');
        return;
      }
      reportSurfaceFailure('document_print', 'print_failed', e);
      setStatus('error');
    }
  }

  return (
    <span className="inline-flex max-w-full flex-col items-start gap-1">
      <Button
        type="button"
        onClick={handleClick}
        disabled={status === 'working'}
        aria-busy={status === 'working'}
        className={className}
        variant={variant}
        size={size}
        data-testid={testId}
      >
        {status === 'working' ? 'Preparing…' : PRINT_PDF_LABEL}
      </Button>
      {(status === 'error' || status === 'stale') && (
        <span role="alert" className="text-xs text-warm-700" data-testid={testId ? `${testId}-error` : undefined}>
          {errorText('document_print', status === 'stale' ? 'module_stale' : 'failed', FALLBACK_SUPPORT_CONTACT)}
        </span>
      )}
    </span>
  );
}
